import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Collapse,
  Stack,
  Avatar,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Battery80 as BatteryIcon,
  LocalGasStation as RangeIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { enhancedVehicleDatabase, getAllVehicles } from './constants/enhancedVehicleData';

const InlineVehicleSelection = ({ selectedVehicle, onVehicleSelect, onClose }) => {
  const [expandedBrands, setExpandedBrands] = useState({});
  const theme = useTheme();

  const brands = Object.keys(enhancedVehicleDatabase);

  const getBrandInitials = (brandName) => {
    if (!brandName) return 'EV';
    const letters = brandName
      .split(' ')
      .filter(Boolean)
      .map((word) => word[0])
      .join('');
    return letters.slice(0, 2).toUpperCase();
  };

  const toggleBrandExpansion = (brand) => {
    setExpandedBrands(prev => ({
      ...prev,
      [brand]: !prev[brand]
    }));
  };

  const handleVehicleSelect = (vehicle, brand) => {
    const brandAssets = enhancedVehicleDatabase[brand] || {};
    const formattedVehicle = {
      ...vehicle,
      brand,
      brandLogo: brandAssets.brandLogo,
      brandColor: brandAssets.color,
      consumption: typeof vehicle.consumption === 'number' ? vehicle.consumption / 1000 : undefined,
      batteryCapacity: vehicle.batteryCapacity,
      range: vehicle.range,
      weight: vehicle.weight
    };
    onVehicleSelect(formattedVehicle);
  };

  const VehicleCard = ({ vehicle, brand }) => {
    const isSelected = selectedVehicle?.id === vehicle.id;
    const brandAssets = enhancedVehicleDatabase[brand] || {};
    const brandColor = brandAssets.color || theme.palette.primary.main;
    const brandLogo = brandAssets.brandLogo;
    const vehicleImage = vehicle.image || brandLogo;
    
    return (
      <Card
        sx={{
          cursor: 'pointer',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? brandColor : 'divider',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: 2,
            borderColor: brandColor
          },
          height: '100%'
        }}
        onClick={() => handleVehicleSelect(vehicle, brand)}
      >
        {/* Vehicle Brand Banner */}
        <Box
          sx={{
            height: 80,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            backgroundColor: `${brandColor}10`,
            borderBottom: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
            px: 1
          }}
        >
          {brandLogo ? (
            <Box
              component="img"
              src={brandLogo}
              alt={`${brand} logo`}
              sx={{ width: 40, height: 40, objectFit: 'contain' }}
            />
          ) : (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: brandColor,
                fontSize: '0.85rem'
              }}
            >
              {brand}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {brand}
          </Typography>
        </Box>
        
        <CardContent sx={{ p: 1.5 }}>
          {vehicleImage && (
            <Box
              component="img"
              src={vehicleImage}
              alt={`${vehicle.name} preview`}
              sx={{
                width: '100%',
                height: 80,
                objectFit: 'contain',
                mb: 1,
                borderRadius: 1,
                backgroundColor: `${brandColor}08`
              }}
            />
          )}

          {/* Vehicle Name & Type */}
          <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>
            {vehicle.name}
          </Typography>
          
          <Chip
            label={vehicle.type}
            size="small"
            sx={{
              backgroundColor: brandColor,
              color: 'white',
              fontSize: '0.65rem',
              height: 18,
              mb: 0.5
            }}
          />
          
          {/* Key Specifications */}
          <Stack spacing={0.3} sx={{ mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RangeIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {vehicle.range}km
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BatteryIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {vehicle.batteryCapacity}kWh
              </Typography>
            </Box>
          </Stack>
          
          {isSelected && (
            <Chip
              label="Selected"
              size="small"
              sx={{
                backgroundColor: brandColor,
                color: 'white',
                mt: 1,
                fontSize: '0.7rem'
              }}
            />
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mb: 1,
        backgroundColor: 'background.paper',
        borderRadius: 2
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '1.1rem' }}>
          Select EV Model
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedVehicle && (
            <Button
              variant="contained"
              size="small"
              onClick={onClose}
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
            >
              Done
            </Button>
          )}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Selected Vehicle Summary */}
      {selectedVehicle && (
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            backgroundColor: 'primary.50',
            border: '1px solid',
            borderColor: 'primary.200'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              variant="rounded"
              src={selectedVehicle.brandLogo || undefined}
              alt={`${selectedVehicle.brand} logo`}
              sx={{
                width: 48,
                height: 36,
                borderRadius: 1,
                backgroundColor: `${(selectedVehicle.brandColor || theme.palette.primary.main)}15`,
                color: selectedVehicle.brandColor || theme.palette.primary.main,
                fontWeight: 600
              }}
            >
              {!selectedVehicle.brandLogo && getBrandInitials(selectedVehicle.brand)}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {selectedVehicle.brand} {selectedVehicle.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {selectedVehicle.range}km • {selectedVehicle.batteryCapacity}kWh
              </Typography>
            </Box>
            
            <Chip 
              label="Selected" 
              color="primary" 
              size="small" 
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          </Box>
        </Paper>
      )}

      {/* Brand Selection */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, fontSize: '1rem' }}>
        Choose by Brand
      </Typography>
      
      {brands.map((brand) => {
        const brandAssets = enhancedVehicleDatabase[brand] || {};
        const brandLogo = brandAssets.brandLogo;
        const brandColor = brandAssets.color || theme.palette.primary.main;
        const brandModels = brandAssets.models || [];

        return (
        <Box key={brand} sx={{ mb: 2 }}>
          {/* Brand Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              p: 1.5,
              borderRadius: 1,
              backgroundColor: expandedBrands[brand] ? brandColor + '10' : 'transparent',
              '&:hover': {
                backgroundColor: brandColor + '20'
              }
            }}
            onClick={() => toggleBrandExpansion(brand)}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                mr: 2,
                bgcolor: `${brandColor}20`,
                color: brandColor,
                fontSize: '0.8rem',
                fontWeight: 600
              }}
            >
              {brandLogo ? (
                <Box
                  component="img"
                  src={brandLogo}
                  alt={`${brand} logo`}
                  sx={{ width: 22, height: 22, objectFit: 'contain' }}
                />
              ) : (
                getBrandInitials(brand)
              )}
            </Avatar>
            
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: brandColor,
                flex: 1
              }}
            >
              {brand}
            </Typography>
            
            <Chip
              label={`${brandModels.length} models`}
              size="small"
              sx={{ mr: 1, fontSize: '0.7rem' }}
            />
            
            <IconButton size="small">
              {expandedBrands[brand] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          {/* Brand Models */}
          <Collapse in={expandedBrands[brand]}>
            <Box sx={{ mt: 1.5, ml: 1.5 }}>
              <Grid container spacing={1.5}>
                {brandModels.map((vehicle) => (
                  <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                    <VehicleCard vehicle={vehicle} brand={brand} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Collapse>
        </Box>
        );
      })}
      
      {/* Quick Stats */}
      <Paper
        sx={{
          p: 1.5,
          mt: 2,
          backgroundColor: 'grey.50',
          borderRadius: 1
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {getAllVehicles().length} Models • {brands.length} Brands • Range: 140-820km
        </Typography>
      </Paper>
    </Paper>
  );
};

export default InlineVehicleSelection;